import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { makeStyles } from 'tss-react/mui';

// import ArchiveIcon from '@mui/icons-material/Archive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockResetIcon from '@mui/icons-material/LockReset';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import SaveIcon from '@mui/icons-material/Save';
import { Box, TextField, Theme, Typography } from '@mui/material';

import { CreateUserFormSubmitProps } from './types';

import {
  getUser,
  getUserRoles,
  deActivateUserAccount,
  resetUserAccount,
  activateUserAccount,
  updateUser,
} from '../../api/usersApi/usersApi';
import FormCancelButton from '../../components/buttons/FormCancelButton';
import FormSelectField from '../../components/formFields/FormSelectField';
import FormSubmitButton from '../../components/buttons/FormSubmitButton';
import FormTextField, { useStyles as useReadOnlyFieldStyles } from '../../components/formFields/FormTextField';
import Loader from '../../components/Loader';
import { CREATE_USER_FORM_DEFAULT_VALUES } from '../../helpers/formSchemas/formDefaultValues';
import { createUserFormSchema } from '../../helpers/formSchemas/formSchemas';
import FormCopyTextField from '../../components/formFields/FormCopyTextField';
import CommonButton from '../../components/buttons/CommonButton';
// import { capitalizeText } from '../../helpers/capitalizeText';
import usePageTitle from '../../hooks/usePageTitle';
import useSnackBar from '../../hooks/useSnackBar';
import Popup from '../../components/Popup';
import { POPUP_DESCRIPTIONS } from '../../constants/popupDescriptions';
import { SelectOption, UserRole } from '../../types';
import { useHasPermission } from '../../hooks/useHasPermission';

const useStyles = makeStyles()((theme: Theme) => ({
  pageContainer: {
    display: 'flex',
  },
  formContainer: {
    display: 'flex',
    flexBasis: '1184px',

    '@media (max-width: 800px)': {
      flexBasis: '100%',
    },
  },
  formWrapper: {
    display: 'flex',
    gap: '64px',

    '@media (max-width: 1000px)': {
      flexDirection: 'column',
      gap: '32px',
    },
  },
  formInnerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    padding: '32px',
    backgroundColor: theme.palette.primary.light,
    borderRadius: '16px',
    flexBasis: '896px',

    '@media (max-width: 1000px)': {
      flexBasis: '416px',
    },

    '@media (max-width: 800px)': {
      flexBasis: '100%',
      padding: '12px',
    },
  },
  formInfoContainer: {
    display: 'flex',
    width: '100%',
    gap: '24px',

    '@media (max-width: 800px)': {
      flexDirection: 'column',
    },
  },
  formPlaceInfoContainer: {
    marginTop: '16px',
    display: 'flex',
    gap: '16px',

    '@media (max-width: 1000px)': {
      flexDirection: 'column',
    },
  },
  fieldHeadline: {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
  },
  formInfoBlock: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '416px',

    '@media (max-width: 1000px)': {
      flexBasis: 0,
    },
  },
  formActionsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '200px',

    '@media (max-width: 800px)': {
      flexBasis: 0,
    },
  },
}));

const UsersEditFormContainer = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { lawyerId } = useParams();
  const { setPageTitle } = usePageTitle();
  const { setAlert } = useSnackBar();

  const canEdit = useHasPermission('Users', 'Edit');

  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [activeAction, setActiveAction] = useState<'reset' | 'deactivate' | 'activate' | null>(null);
  const [roleOptions, setRoleOptions] = useState<SelectOption[]>([]);
  const [isEditingSelf, setIsEditingSelf] = useState<boolean>(false);
  const [userStatus, setUserStatus] = useState<string>('');
  const isUserActive = userStatus.toLowerCase() === 'active';
  const isUserInactive = userStatus.toLowerCase() === 'inactive';

  // Only admins can deactivate or activate users (role of the logged-in user, saved at login)
  const isAdmin = localStorage.getItem('userRole')?.toLowerCase() === 'admin';

  const readOnlyFieldStyles = useReadOnlyFieldStyles({});

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(createUserFormSchema),
    defaultValues: CREATE_USER_FORM_DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = formMethods;

  const onSubmitForm = (values: CreateUserFormSubmitProps) => {
    setIsFormSubmitting(true);

    const submitData = {
      firstName: values.firstName,
      lastName: values.lastName,
      role: 'FirmAdmins',
      roleId: Number(values.roleId),
    };

    updateUser(lawyerId as string, submitData)
      .then((res) => {
        if (res.data) {
          setIsFormSubmitting(false);
          navigate('/users');

          setAlert((prev) => ({
            ...prev,
            message: 'User account updated successfully.',
            type: 'success',
          }));
        }
      })
      .catch((err) => {
        setIsFormSubmitting(false);
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message,
          type: 'error',
        }));
      });
  };

  const handleResetUserAccount = () => {
    handleClosePopup();

    resetUserAccount(lawyerId as string)
      .then((res) => {
        if (res.status === 204) {
          navigate('/users');

          setAlert((prev) => ({
            ...prev,
            message: 'User account has been reset successfully.',
            type: 'success',
          }));
        }
      })
      .catch((err) => {
        setAlert((prev) => ({
          ...prev,
          message: err.response?.data.message,
          type: 'error',
        }));
      });
  };

  const handleDeactivateUserAccount = () => {
    handleClosePopup();

    deActivateUserAccount(lawyerId as string)
      .then(() => {
        navigate('/users');

        setAlert((prev) => ({
          ...prev,
          message: 'User account has been deactivated successfully.',
          type: 'success',
        }));
      })
      .catch((err) => {
        setAlert((prev) => ({
          ...prev,
          message: err.response?.data.message,
          type: 'error',
        }));
      });
  };

  const handleActivateUserAccount = () => {
    handleClosePopup();

    activateUserAccount(lawyerId as string)
      .then(() => {
        navigate('/users');

        setAlert((prev) => ({
          ...prev,
          message: 'User account has been activated successfully.',
          type: 'success',
        }));
      })
      .catch((err) => {
        setAlert((prev) => ({
          ...prev,
          message: err.response?.data.message,
          type: 'error',
        }));
      });
  };

  const handleCancelChanges = () => reset();
  const handleOpenPopup = (action: 'reset' | 'deactivate' | 'activate') => setActiveAction(action);

  // Users can't deactivate their own account (also enforced by the API)
  const handleDeactivateClick = () => {
    if (isEditingSelf) {
      setAlert((prev) => ({
        ...prev,
        message: 'You cannot deactivate your own account',
        type: 'error',
      }));
      return;
    }
    handleOpenPopup('deactivate');
  };
  const handleClosePopup = () => setActiveAction(null);

  const popupPropsByAction = {
    reset: { ...POPUP_DESCRIPTIONS.lawyersPage, onConfirm: handleResetUserAccount },
    deactivate: { ...POPUP_DESCRIPTIONS.deactivateUserAccount, onConfirm: handleDeactivateUserAccount },
    activate: { ...POPUP_DESCRIPTIONS.activateUserAccount, onConfirm: handleActivateUserAccount },
  };

  useEffect(() => {
    getUserRoles().then((res) => {
      if (res.data?.roles) {
        setRoleOptions(
          res.data.roles.map((r) => ({ id: String(r.id), label: r.roleName }))
        );
      }
    });
  }, []);

  useEffect(() => {
    const loggedInUserId = localStorage.getItem('userId');
    
    getUser(lawyerId as string).then((res) => {
      if (res.data) {
        reset({
          id: res.data.id,
          firstName: res.data.firstName,
          lastName: res.data.lastName,
          email: res.data.email,
          phone: res.data.phone,
          role: res.data.role,
          roleId: res.data.roles?.length
            ? String((res.data.roles[0] as UserRole).id ?? '')
            : '',
          firmId: String(res.data.firm.id),
        });

        setIsEditingSelf(loggedInUserId === String(res.data.id));
        setUserStatus(res.data.status ?? '');

        setPageTitle && setPageTitle(`${res.data.firstName} ${res.data.lastName}`);
        setIsDataLoading(false);
      }
    });

    return () => setPageTitle && setPageTitle('');
  }, [lawyerId]);

  return (
    <>
      {isDataLoading ? (
        <Loader colorType="warning" />
      ) : (
        <>
          <Box className={styles.classes.pageContainer}>
            <FormProvider {...formMethods}>
              <form className={styles.classes.formContainer}>
                <Box className={styles.classes.formInfoContainer}>
                  <Box className={styles.classes.formInnerWrapper}>
                    <Box className={styles.classes.formWrapper}>
                      <Box className={styles.classes.formInfoBlock}>
                        <Typography className={styles.classes.fieldHeadline}>Identity</Typography>
                        <Box mt="16px">
                          <FormCopyTextField entityName="User" name="id" label="Id" isReadOnly />
                        </Box>
                        <Box mt="24px">
                          <FormTextField name="firstName" label="First Name *" disabled={!canEdit} />
                        </Box>
                        <Box mt="24px">
                          <FormTextField name="lastName" label="Last Name *" disabled={!canEdit} />
                        </Box>

                        <Typography className={styles.classes.fieldHeadline} mt="32px">
                          Credentials
                        </Typography>

                        <Box mt="16px">
                          <FormTextField name="email" label="Email Address *" disabled />
                        </Box>
                        <Box mt="24px">
                          <FormTextField name="phone" label="Mobile Number *" disabled />
                        </Box>
                      </Box>

                      <Box className={styles.classes.formInfoBlock}>
                        <Typography className={styles.classes.fieldHeadline}>Role</Typography>

                        <Box mt="16px">
                          <FormSelectField
                            options={roleOptions}
                            name="roleId"
                            label="Role *"
                            width="100%"
                            disabled={isEditingSelf || !canEdit}
                          />
                        </Box>

                        <Typography className={styles.classes.fieldHeadline} mt="32px">
                          Status
                        </Typography>

                        <Box mt="16px">
                          <TextField
                            fullWidth
                            variant="outlined"
                            value={userStatus}
                            disabled
                            className={readOnlyFieldStyles.classes.defaultTextField}
                          />
                        </Box>
                      </Box>
                    </Box>

                    {canEdit && (
                    <Box display="flex" width="100%" justifyContent="center">
                      <Box
                        display="flex"
                        mt="32px"
                        maxWidth="416px"
                        flexDirection="column"
                        gap="16px"
                        width="100%"
                      >
                        <FormSubmitButton
                          disabled={!isValid || !isDirty || isFormSubmitting}
                          buttonIcon={<SaveIcon />}
                          buttonText="Save"
                          onClick={handleSubmit(onSubmitForm)}
                        />
                        <FormCancelButton disabled={!isDirty} onClick={handleCancelChanges} />
                      </Box>
                    </Box>
                    )}
                  </Box>

                  {canEdit && (
                  <Box className={styles.classes.formActionsWrapper}>
                    <Typography className={styles.classes.fieldHeadline}>Actions</Typography>

                    <Box mt="16px">
                      <CommonButton
                        type="success"
                        buttonText="reset account"
                        buttonIcon={<LockResetIcon />}
                        // An inactive user can't sign in, so resetting their account access is not allowed
                        disabled={isUserInactive}
                        onButtonClick={() => handleOpenPopup('reset')}
                      />
                    </Box>
                    {/* Admins only: one button matching the loaded user's current status */}
                    {isAdmin && isUserActive && (
                    <Box mt="16px">
                      <CommonButton
                        type="success"
                        buttonText="Deactivate"
                        buttonIcon={<PersonOffIcon />}
                          onButtonClick={handleDeactivateClick}
                      />
                    </Box>
                    )}
                    {isAdmin && isUserInactive && (
                    <Box mt="16px">
                      <CommonButton
                        type="success"
                        buttonText="Activate"
                        buttonIcon={<CheckCircleIcon />}
                        onButtonClick={() => handleOpenPopup('activate')}
                      />
                    </Box>
                    )}
                    {/* <Box mt="16px">
                      <CommonButton
                        type="error"
                        buttonText="archive"
                        buttonIcon={<ArchiveIcon />}
                        disabled
                      />
                    </Box> */}
                  </Box>
                  )}
                </Box>
              </form>
            </FormProvider>
            <Popup
              isOpen={!!activeAction}
              headlineText={activeAction ? popupPropsByAction[activeAction].headlineText : ''}
              contentText={activeAction ? popupPropsByAction[activeAction].contentText : ''}
              onSecondaryButtonClick={handleClosePopup}
              onMainButtonClick={activeAction ? popupPropsByAction[activeAction].onConfirm : handleClosePopup}
            />
          </Box>
        </>
      )}
    </>
  );
};

export default UsersEditFormContainer;
